import { navigate } from 'gatsby';
import type { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import IconButton from '@components/IconButton';

import { ALERTS } from '@constants/routes';

import { useCuration } from '@hooks/useCuration';
import { useIsMobile } from '@hooks/useIsMobile';

import { formatDate, formatName } from '@utils/format';

import type { GetAllPatientUsersQuery } from 'generated/user.generated';

import AlertedPatientMobileRow from './MobileRow';

const messages = defineMessages({
  title: {
    id: 'patientAlertTable.title',
    defaultMessage: 'My Patients with Open Alerts',
  },
  noData: {
    id: 'patientAlertTable.noData',
    defaultMessage: 'No Patients',
  },
  'column.dob': {
    id: 'patientAlertTable.column.dob',
    defaultMessage: 'Date Of Birth',
  },
  'column.lastAlertDate': {
    id: 'patientAlertTable.column.lastAlertDate',
    defaultMessage: 'Last Alert Date',
  },
  'column.openAlertCount': {
    id: 'patientAlertTable.column.openAlertCount',
    defaultMessage: 'Number Of Open Alerts',
  },
  'column.patient': {
    id: 'patientAlertTable.column.patient',
    defaultMessage: 'Patient',
  },
});

type RowData = {
  user_id: string;
  name?: string | null;
  dob?: string | null;
  open_alert_count?: number;
  last_alert_date?: string | null;
};

interface AlertedPatientTableProps {
  data: GetAllPatientUsersQuery['getAllPatientUsers'];
}

export default function AlertedPatientTable({
  data,
}: AlertedPatientTableProps) {
  const { formatMessage } = useIntl();
  const { viewAsPatient } = useCuration();
  const isMobile = useIsMobile('md');

  const tableData = data.map(
    ({
      first_name,
      last_alert_date,
      last_name,
      open_alert_count,
      user_id,
      vitall_patient,
    }) => {
      return {
        user_id,
        name: formatName(first_name, last_name),
        dob: vitall_patient?.date_of_birth,
        open_alert_count,
        last_alert_date,
      };
    }
  );

  const handleRowClick: MUIDataTableOptions['onRowClick'] = (
    _rowData,
    { dataIndex }
  ) => {
    const { user_id } = tableData[dataIndex];
    viewAsPatient(user_id);
    navigate(ALERTS);
  };

  return (
    <MuiDataTable
      columns={[
        {
          name: 'name',
          className: 'blue-hover',
          label: formatMessage(messages['column.patient']),
          options: {
            sort: true,
          },
        },
        {
          name: 'dob',
          className: 'date',
          label: formatMessage(messages['column.dob']),
          options: {
            sort: true,
            customBodyRender: (value: RowData['last_alert_date']) =>
              value ? formatDate({ date: value }) : '-',
          },
        },
        {
          name: 'open_alert_count',
          className: 'small',
          label: formatMessage(messages['column.openAlertCount']),
          options: {
            sort: true,
          },
        },
        {
          name: 'last_alert_date',
          className: 'date',
          label: formatMessage(messages['column.lastAlertDate']),
          options: {
            sort: true,
            customBodyRender: (value: RowData['last_alert_date']) =>
              value ? formatDate({ date: value, formatter: 'datetime' }) : '-',
          },
        },
        {
          name: 'user_id',
          options: {
            display: false,
          },
        },
        {
          name: 'user_id',
          options: {
            customHeadLabelRender: () => null,
            customBodyRender: (user_id: RowData['user_id']) => {
              return (
                <IconButton
                  color="text.secondary"
                  iconType="fad"
                  name="chevron-right"
                  onClick={() => {
                    viewAsPatient(user_id);
                    navigate(ALERTS);
                  }}
                />
              );
            },
          },
        },
      ]}
      data={tableData}
      options={{
        search: true,
        sort: true,
        filter: true,
        sortOrder: {
          name: 'name',
          direction: 'desc',
        },
        onRowClick: handleRowClick,
        textLabels: {
          body: {
            noMatch: formatMessage(messages.noData),
          },
        },
        ...(isMobile
          ? {
              customRowRender: (data: React.ReactNode[], dataIndex: number) => (
                <AlertedPatientMobileRow
                  data={data}
                  dataIndex={dataIndex}
                  total={tableData.length}
                />
              ),
            }
          : { customRowRender: undefined }),
      }}
      title={formatMessage(messages.title)}
    />
  );
}
